import { useState, useRef, useEffect } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { chatAssistant } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Send, Bot, User, Sparkles } from 'lucide-react';
import { Markdown } from '@/components/markdown';

type Message = { role: 'user' | 'assistant'; content: string };

const STARTERS = [
  'What fire extinguisher should I use for an electrical fire?',
  'What are the beer line cleaning requirements?',
  'How do I do the water-break test on glasses?',
  'What ID can I accept for age verification?',
];

export default function TrainingAssistant() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamContent, setStreamContent] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamContent]);

  const send = async (text?: string) => {
    const messageText = (text || input).trim();
    if (!messageText || streaming) return;
    setInput('');

    const userMsg: Message = { role: 'user', content: messageText };
    const history = [...messages, userMsg];
    setMessages(history);
    setStreaming(true);
    setStreamContent('');

    try {
      const stream = chatAssistant({
        messages: history.map(m => ({ role: m.role, content: m.content })),
      });

      let full = '';
      for await (const chunk of stream) {
        full += chunk;
        setStreamContent(full);
      }

      setMessages(prev => [...prev, { role: 'assistant', content: full }]);
      setStreamContent('');
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I couldn\'t get a response. Please try again.' }]);
      setStreamContent('');
    } finally {
      setStreaming(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-card border-b border-border shrink-0">
        <div className="px-6 md:px-8 py-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">Training Assistant</h1>
              <p className="text-xs text-muted-foreground">Answers from The Rattlebone handbook & SOPs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
        <div className="max-w-2xl mx-auto space-y-4">
          {messages.length === 0 && !streaming && (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Bot className="w-7 h-7 text-primary" />
              </div>
              <h2 className="font-semibold text-foreground mb-2">Ask anything about your training</h2>
              <p className="text-sm text-muted-foreground mb-6">I'll answer based on The Rattlebone's training content and SOPs.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {STARTERS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    className="text-left text-sm border border-border bg-card hover:border-primary/40 hover:bg-card/80 px-4 py-3 rounded-md text-muted-foreground hover:text-foreground transition-colors"
                  >
                    "{s}"
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${msg.role === 'user' ? 'bg-primary' : 'bg-primary/15'}`}>
                {msg.role === 'user'
                  ? <User className="w-4 h-4 text-primary-foreground" />
                  : <Bot className="w-4 h-4 text-primary" />}
              </div>
              <div className={`max-w-[85%] rounded-lg p-3 text-sm ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-foreground'}`}>
                {msg.role === 'assistant' ? <Markdown>{msg.content}</Markdown> : msg.content}
              </div>
            </div>
          ))}

          {/* Streaming response */}
          {streaming && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center bg-primary/15">
                <Bot className="w-4 h-4 text-primary" />
              </div>
              <div className="max-w-[85%] rounded-lg p-3 text-sm bg-card border border-border text-foreground">
                {streamContent ? (
                  <Markdown>{streamContent}</Markdown>
                ) : (
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                )}
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input */}
      <div className="shrink-0 bg-card border-t border-border px-4 md:px-8 py-4">
        <div className="max-w-2xl mx-auto flex gap-2">
          <Textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask a training question..."
            className="resize-none min-h-[44px] max-h-32 text-sm"
            rows={1}
          />
          <Button
            onClick={() => send()}
            disabled={!input.trim() || streaming}
            className="bg-primary text-primary-foreground h-11 w-11 p-0 shrink-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground text-center mt-2">Answers are based on training content added by your admin. Always verify with your manager for critical decisions.</p>
      </div>
    </div>
  );
}
